import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import IconCloseCircle from '@/assets/Icons/IconCloseCircle';
import { useEstimation } from '@/context/estimationContext';
import { useEstimationWizard } from '@/context/estimationWizardContext';
import { useMessages } from '@/hooks/messages';
import { useLocationMatch } from '@/hooks/useLocationMatch';
import { EstimationNavigationItem } from '@/pages/estimation/components/estimationNavigation/estimationNavigationItem';
import { getSetupSteps } from '@/pages/estimation/components/estimationNavigation/model';

import { NavigationMessages } from './messages';

interface ISetupNavigationProps {
  isSidebarOpen: boolean;
  onItemClick?: () => void;
}

export const SetupNavigation: React.FC<ISetupNavigationProps> = ({
  isSidebarOpen,
  onItemClick,
}) => {
  const {
    actions: { cancelWizard },
  } = useEstimationWizard();
  const {
    state: { savedEstimation, unsavedEstimation },
    actions: { resetEstimationState },
  } = useEstimation();

  const { hasPartialMatch } = useLocationMatch();

  const [isActive, setIsActive] = useState(false);

  const messages = useMessages(NavigationMessages);
  const setupSteps = useMemo(() => getSetupSteps(messages), [messages]);

  useEffect(() => {
    // Show setup navigation when on any setup step or choose-mode
    setIsActive(
      hasPartialMatch('/estimation/new/basics') || hasPartialMatch('/estimation/new/choose-mode'),
    );
  }, [hasPartialMatch]);

  const showCancelEstimation = !savedEstimation?.estimation.id;

  const handleOnCancel = useCallback(() => {
    cancelWizard();
    resetEstimationState();
  }, [cancelWizard, resetEstimationState]);

  const flattenedChildren = useMemo(
    () => setupSteps.flatMap((s) => s.children || []),
    [setupSteps],
  );
  const indexOfBasicsProject = useMemo(
    () => flattenedChildren.findIndex((s) => s.link === '/estimation/new/basics/project'),
    [flattenedChildren],
  );
  const indexOfBasicsProjectType = useMemo(
    () => flattenedChildren.findIndex((s) => s.link === '/estimation/new/basics/project-type'),
    [flattenedChildren],
  );
  const indexOfBasicsMethodology = useMemo(
    () => flattenedChildren.findIndex((s) => s.link === '/estimation/new/basics/methodology'),
    [flattenedChildren],
  );

  const isChildStepDisabled = useCallback(
    (childIndex: number) => {
      const hasProjectInfo =
        !!unsavedEstimation?.project.name && !!unsavedEstimation?.estimation.planningResponsibleId;
      const hasProjectType = !!unsavedEstimation?.project.projectTypeId;
      const hasMethodology =
        !!unsavedEstimation?.estimation.methodologyId &&
        !!unsavedEstimation?.estimation.estimationModelId;

      if (!hasProjectInfo && childIndex > indexOfBasicsProject) return true;
      if (!hasProjectType && childIndex > indexOfBasicsProjectType) return true;
      if (!hasMethodology && childIndex > indexOfBasicsMethodology) return true;

      return false;
    },
    [unsavedEstimation, indexOfBasicsProject, indexOfBasicsProjectType, indexOfBasicsMethodology],
  );

  if (!isActive) {
    return null;
  }

  return (
    <div
      className={`${
        isSidebarOpen ? 'max-h-[1500px]' : 'max-h-0'
      } grid w-full overflow-hidden transition-all duration-300 ease-in-out`}
    >
      {setupSteps.map((step, index) => {
        const parentIsActive = hasPartialMatch(step.link);
        const isOnChooseMode = hasPartialMatch('/estimation/new/choose-mode');
        // Show children if parent is active OR if we're on choose-mode page
        const shouldShowChildren = parentIsActive || isOnChooseMode;

        return (
          <div key={step.link} className='gap-1 first:mt-5'>
            <EstimationNavigationItem
              index={index}
              key={step.label}
              label={step.label}
              navigateTo={step.link}
              disabled={!savedEstimation?.estimation.id && index > 0}
              isOmitted={step.isOmitted}
              ignoreNumbering={step.ignoreNumbering}
              isActive={parentIsActive && !step.isOmitted}
              hasChildren={Boolean(step.children)}
              onItemClick={onItemClick}
            />

            {shouldShowChildren &&
              step.children?.map((child, childIndex) => (
                <EstimationNavigationItem
                  key={child.label}
                  label={child.label}
                  index={childIndex}
                  navigateTo={child.link}
                  disabled={isChildStepDisabled(childIndex)}
                  isOmitted={child.isOmitted}
                  ignoreNumbering={child.ignoreNumbering}
                  hasChildren={false}
                  isChild={true}
                  parentIndex={index}
                  onItemClick={onItemClick}
                />
              ))}
          </div>
        );
      })}

      {showCancelEstimation && (
        <Link
          onClick={handleOnCancel}
          to='/'
          className='border-secondary shadow-steps dark:shadow-steps-dark mt-2 mb-1 flex items-center justify-center gap-2 rounded-md border border-1 bg-(--nav-item-bg) py-1 text-center text-[10px] font-semibold text-(--text-secondary)'
        >
          <IconCloseCircle />
          {messages.cancelEstimation()}
        </Link>
      )}
    </div>
  );
};
